import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    IconButton,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useEffect, useMemo, useState } from 'react';
import { useCommonStyles } from '../../styles/GenericStyle';
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { DASH_PLACEHOLDER } from '../../utils/missingValue';
import { useReportNoSeedForMaterials, useValidateScannedMaterialForSplot } from '../../api/vegApl';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useGlobalState } from '../../contexts/GlobalState';
import useGetSortedRefCrops from '../../api/useGetSortedRefCrops';
import ScanInput from '../ScanInput';
import FullScreenActionLoader from '../common/FullScreenActionLoader';

// Mirrors the real API response shape — swap MOCK_MATERIALS with API call when ready
interface HarvestInstance {
    material_harvest_id: number;
    splot_id: string | null;
    required_grams: string;
    registered_grams: string;
    harvest_status: 'Registered' | 'Processing' | 'No Seed' | null;
    is_current_harvest: boolean;
}

interface MaterialDetail {
    matid: string;
    trial_id: string;
    splot_id: string;
    crop_code: string;
    site_code: string;
    material_id: number;
    total_registered_grams: string;
    total_required_grams: string;
    harvests: HarvestInstance[];
}

interface ReportNoSeedPayload {
    matid: string;
    remark_id: number;
    another_harvest_required: boolean;
    user_id: number;
}

interface ReportNoSeedPopupProps {
    open: boolean;
    onClose: () => void;
    onSubmit?: (payload: ReportNoSeedPayload) => void;
}

const ReportNoSeedPopup = ({ open, onClose, onSubmit }: ReportNoSeedPopupProps) => {
    const classes = useCommonStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { showSnackbar } = useSnackbar();
    const { reportNoSeedRemarks, state: globalState } = useGlobalState();
    const { data: crops = [] } = useGetSortedRefCrops(globalState?.siteSelected);

    const [step, setStep] = useState<1 | 2>(1);
    const [materialIdInput, setMaterialIdInput] = useState('');
    const [selectedMaterial, setSelectedMaterial] = useState<MaterialDetail | null>(null);
    const [selectedRemark, setSelectedRemark] = useState('');
    const [remarkError, setRemarkError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [isReportingNoSeed, setIsReportingNoSeed] = useState(false);

    const selectedHarvest = useMemo(
        () => selectedMaterial?.harvests?.find((h) => {
            const status = String(h?.harvest_status ?? '').trim().toLowerCase();
            return status !== 'registered' && status !== 'no seed';
        }) ?? null,
        [selectedMaterial],
    );

    const hasEligibleHarvest = Boolean(selectedHarvest);
    
    const cropName = useMemo(() => {
        if (!selectedMaterial?.crop_code) return DASH_PLACEHOLDER;
        const crop = crops.find((c: any) => c.crop_xrd_oid === selectedMaterial.crop_code);
        return crop?.name ?? selectedMaterial.crop_code;
    }, [selectedMaterial?.crop_code, crops]);
    
    const remarkOptions = useMemo(() => {
        const opts = (reportNoSeedRemarks ?? [])
            .filter(r => r.remark_name && r.remark_id != null)
            .map(r => ({ label: r.remark_name as string, value: String(r.remark_id) }));
        opts.sort((a, b) => a.label.localeCompare(b.label));
        return opts;
    }, [reportNoSeedRemarks]);

    const canSubmitNoSeed = hasEligibleHarvest && Boolean(selectedRemark) && !isReportingNoSeed;
    const isActionInProgress = isValidating || isReportingNoSeed;

    const handleReset = () => {
        setStep(1);
        setMaterialIdInput('');
        setSelectedMaterial(null);
        setSelectedRemark('');
        setRemarkError('');
        setSuccessMessage('');
        setIsValidating(false);
        setIsReportingNoSeed(false);
    };

    useEffect(() => {
        if (!open) {
            handleReset();
        }
    }, [open]);

    const { mutate: validateScannedMaterial } = useValidateScannedMaterialForSplot({
        mutation: {
            onSuccess: (response: any) => {
                const rawData = response?.data?.data ?? response?.data;

                const apiErrorMessage =
                    response?.data?.error ||
                    response?.data?.detail ||
                    (typeof rawData === 'object' ? rawData?.error || rawData?.detail : '');

                if (apiErrorMessage) {
                    setIsValidating(false);
                    showSnackbar({ message: apiErrorMessage, severity: 'error' });
                    return;
                }

                const materialFromApi =
                    rawData &&
                        typeof rawData === 'object' &&
                        typeof rawData?.matid === 'string' &&
                        Array.isArray(rawData?.harvests)
                        ? (rawData as MaterialDetail)
                        : null;

                if (!materialFromApi) {
                    setIsValidating(false);
                    showSnackbar({ message: 'Enter a valid MatID', severity: 'error' });
                    return;
                }

                setIsValidating(false);
                setSelectedMaterial(materialFromApi);
                setStep(2);
            },
            onError: (error: any) => {
                setIsValidating(false);
                const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to validate scanned material';
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    const { mutate: reportNoSeedForMaterial } = useReportNoSeedForMaterials({
        mutation: {
            onSuccess: (response: any, variables) => {
                setIsReportingNoSeed(false);
                const apiMessage = response?.data?.message || response?.data?.detail || 'No Seed reported successfully';
                onSubmit?.(variables.data);
                showSnackbar({ message: apiMessage, severity: 'success' });
                onClose();
            },
            onError: (error: any) => {
                setIsReportingNoSeed(false);
                const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to report no seed';
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    const handleValidateAndNext = () => {
        setSuccessMessage('');

        const normalizedMaterialId = materialIdInput.trim().toUpperCase();

        if (!normalizedMaterialId) {
            showSnackbar({ message: 'Please enter or scan Material ID', severity: 'warning' });
            return;
        }

        setIsValidating(true);
        validateScannedMaterial({ data: { matid: normalizedMaterialId } });
    };

    const handleReportNoSeed = () => {
        setRemarkError('');
        setSuccessMessage('');

        if (!hasEligibleHarvest) {
            showSnackbar({ message: 'No eligible harvest available to report as No Seed', severity: 'warning' });
            return;
        }

        if (!selectedRemark) {
            setRemarkError('Please select a remark');
            return;
        }

        if (!globalState?.user_id_db) {
            showSnackbar({ message: 'Unable to identify current user', severity: 'error' });
            return;
        }

        if (!selectedMaterial || !selectedHarvest) {
            return;
        }

        const payload: ReportNoSeedPayload = {
            matid: selectedMaterial.matid,
            remark_id: Number(selectedRemark),
            another_harvest_required: true,
            user_id: Number(globalState.user_id_db),
        };

        setIsReportingNoSeed(true);
        reportNoSeedForMaterial({ data: payload });
    };

    const detailsRow = (
        label: string,
        value: string,
    ) => (
        <Box className={classes.labelNoto12Bold400} sx={{ minWidth: 120, height: 38, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" className={classes.labelNoto14Bold700} sx={value === DASH_PLACEHOLDER ? { fontSize: 16, fontWeight: 600 } : undefined}>
                {value}
            </Typography>
        </Box>
    );

    return (
        <>
        <FullScreenActionLoader open={isActionInProgress} />
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen={fullScreen}
            maxWidth="sm"
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: fullScreen ? 0 : 1.5,
                    height: !fullScreen && step === 1 ? 280 : 'auto',
                },
            }}
        >
            <DialogTitle
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 2,
                    height: 91,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: '#FFEDD4',
                            }}
                        >
                            <WarningAmberOutlinedIcon color="warning" fontSize="small" />
                        </Box>
                        <Box>
                            <Typography variant="h6" className={classes.labelNoto20Bold700}>
                                Report No Seed
                            </Typography>
                            <Typography variant="body2" color="text.secondary" className={classes.labelNoto14Bold400}>
                                {step === 1 ? 'Enter or Scan Material ID' : 'Confirm material details and remarks'}
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton onClick={onClose} size="small" aria-label="close report no seed popup">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent
                sx={{
                    px: { xs: 2, md: 3 },
                    pt: 0,
                    pb: 3,
                    bgcolor: 'background.default',
                }}
            >
                {successMessage ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMessage}
                    </Alert>
                ) : null}

                {step === 1 ? (
                    <Box sx={{ mt: '20px' }}>
                        <Typography variant="body1" sx={{ mb: 1 }} className={classes.labelNoto14Bold400}>
                            Material ID <Box component="span" sx={{ color: theme.palette.error.main }}>*</Box>
                        </Typography>
                        <ScanInput
                            submitOnScan
                            scanPickupEnabled={true}
                            defaultValue={materialIdInput}
                            onChange={(val: string) => setMaterialIdInput(val)}
                            onScan={(val: string) => {
                                setMaterialIdInput(val);
                                handleValidateAndNext();
                            }}
                            onEnterPress={handleValidateAndNext}
                            manualEdit={true}
                            error={false}
                            label=""
                            placeholder="Enter or Scan Material ID"
                            errorText=""
                        />
                    </Box>
                ) : (
                    <Stack spacing={2} sx={{ mt: '24px' }}>
                        <Box sx={{
                            gap: '12px',
                            borderRadius: '10px',
                            opacity: 1,
                            pt: '17px',
                            pr: '17px',
                            pb: '17px',
                            pl: '17px',
                            border: '1px solid #E5E7EB',
                            bgcolor: '#F5F5F5',
                        }}>
                            <Typography variant="subtitle2" className={classes.labelNoto14Bold700} sx={{mb: '12px' }}>
                                Material Details
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', rowGap: '16px', columnGap: '16px' }}>
                                {detailsRow('Material ID', selectedMaterial?.matid ?? DASH_PLACEHOLDER)}
                                {detailsRow('Crop', cropName)}
                                {detailsRow('Site', selectedMaterial?.site_code ?? DASH_PLACEHOLDER)}
                                {detailsRow('Trial ID', selectedMaterial?.trial_id ?? DASH_PLACEHOLDER)}
                                {detailsRow('Registered Grams', selectedMaterial?.total_registered_grams != null ? `${selectedMaterial.total_registered_grams} g` : DASH_PLACEHOLDER)}
                                {detailsRow('Required Grams', selectedMaterial?.total_required_grams != null ? `${selectedMaterial.total_required_grams} g` : DASH_PLACEHOLDER)}
                            </Box>
                        </Box>

                        <Box sx={{
                            opacity: 1,
                            gap: '12px',
                            borderRadius: '10px',
                            pt: '17px',
                            pr: '17px',
                            pb: '1px',
                            pl: '17px',
                            bgcolor: '#EFF6FF',
                            border: '1px solid #BEDBFF',
                        }}>
                            <Typography variant="subtitle2" className={classes.labelNoto14Bold700} sx={{ mb: 1.5 }}>
                                Harvest Information
                            </Typography>
                            <Stack spacing={1.25}>
                                {(selectedMaterial?.harvests ?? []).map((harvest, index) => {
                                    const isTarget = harvest.material_harvest_id === selectedHarvest?.material_harvest_id;
                                    const harvestStatus = String(harvest.harvest_status ?? '').trim().toLowerCase();
                                    const isGreenStatus = harvestStatus === 'registered' || harvestStatus === 'no seed';
                                    return (
                                        <Box
                                            key={harvest.material_harvest_id}
                                            sx={{
                                                height: 66,
                                                borderRadius: '4px',
                                                border: isGreenStatus ? '1px solid #BBF7D0' : '1px solid #FF8904',
                                                boxShadow: isGreenStatus ? 'none' : '0px 1px 2px -1px #0000001A',
                                                bgcolor: isGreenStatus ? '#F0FDF4' : '#FFF7ED',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                                px: '12px',
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" className={classes.labelNoto14Bold600}>
                                                    {`Harvest #${index + 1}`}
                                                    {isTarget ? (
                                                        <Box
                                                            component="span"
                                                            className={classes.labelNoto12Bold500}
                                                            sx={{
                                                                ml: 1,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                px: 1,
                                                                height: 20,
                                                                borderRadius: '4px',
                                                                bgcolor: '#585858',
                                                                color: '#FFFFFF',
                                                                fontSize: '12px',
                                                                fontWeight: 500,
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            To be marked as No Seed
                                                        </Box>
                                                    ) : null}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" className={classes.labelNoto12Bold400}>
                                                    {`SP Lot - ${harvest.splot_id ?? selectedMaterial?.splot_id ?? 'N/A'}, ${harvest.registered_grams} registered grams`}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={harvest.harvest_status ?? 'Pending'}
                                                size="small"
                                                className={classes.labelNoto12Bold500}
                                                sx={{
                                                    minWidth: 88,
                                                    height: 24,
                                                    borderRadius: '4px',
                                                    bgcolor: isGreenStatus ? '#DCFCE7' : '#FEF3C6',
                                                    color: isGreenStatus ? '#15803D' : '#D97706',
                                                    '& .MuiChip-label': {
                                                        px: 1,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Stack>
                            <Box className={classes.labelNoto14Bold400} sx={{ mt: 1.25, display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Harvests:
                                </Typography>
                                <Typography variant="body2" sx={{fontWeight:600}}>
                                    {selectedMaterial?.harvests.length ?? 0}
                                </Typography>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="body2" className={classes.labelNoto14Bold400} sx={{ mb: '6px'}}>
                                Select Remarks <span style={{ color: '#d32f2f' }}>*</span>
                            </Typography>
                            <FormControl fullWidth error={!!remarkError}>
                                <Select
                                    displayEmpty
                                    value={selectedRemark}
                                    onChange={(event) => {
                                        setSelectedRemark(event.target.value);
                                        if (remarkError) {
                                            setRemarkError('');
                                        }
                                    }}
                                    input={<OutlinedInput size="small" />}
                                    renderValue={(selected) => {
                                        const found = remarkOptions.find(o => o.value === selected);
                                        return found ? (
                                            <span>{found.label}</span>
                                        ) : (
                                            <span style={{ color: '#9e9e9e' }}>Select a Remark</span>
                                        );
                                    }}
                                >
                                    {remarkOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {remarkError ? <FormHelperText>{remarkError}</FormHelperText> : null}
                            </FormControl>
                        </Box>
                    </Stack>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    justifyContent: 'space-between',
                }}
            >
                <Box />

                {step === 1 ? (
                    <Button
                        variant="contained"
                        className={classes.greenButton}
                        sx={(muiTheme) => ({
                            ...MtComStyleObj(muiTheme).disabledGreenBtnStyle,
                            minWidth: 96,
                        })}
                        disabled={isValidating}
                        onClick={handleValidateAndNext}
                    >
                        {isValidating ? 'Validating...' : 'Next'}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        className={classes.greenButton}
                        sx={(muiTheme) => ({
                            ...MtComStyleObj(muiTheme).disabledGreenBtnStyle,
                            minWidth: 160,
                        })}
                        disabled={!canSubmitNoSeed}
                        onClick={handleReportNoSeed}
                    >
                        {isReportingNoSeed ? 'Submitting...' : 'Report No Seed'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
        </>
    );
};

export default ReportNoSeedPopup;
